import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { resolveImg } from "@/lib/api";
import { Plus, Trash2, Save, LogOut, ExternalLink, X, Upload } from "lucide-react";

const TABS = ["site", "tracks", "shows", "gallery"];

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const [tab, setTab] = useState("site");

  return (
    <div className="min-h-screen bg-white text-black font-mono">
      <header className="border-b-4 border-black flex items-center justify-between px-5 md:px-10 h-16 sticky top-0 bg-white z-30">
        <div className="flex items-center gap-3">
          <span className="font-scribble text-3xl">LilBrain jr</span>
          <span className="text-xs uppercase tracking-[0.3em] border-2 border-black px-2 py-1">control room</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" target="_blank" data-testid="view-site-link" className="hidden sm:flex items-center gap-2 border-2 border-black px-3 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-white transition-colors">
            view site <ExternalLink size={13} strokeWidth={3} />
          </Link>
          <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-2 border-2 border-black px-3 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-white transition-colors">
            <LogOut size={13} strokeWidth={3} /> logout
          </button>
        </div>
      </header>

      <div className="flex flex-wrap gap-0 border-b-4 border-black">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`px-6 py-4 uppercase tracking-widest text-sm border-r-4 border-black transition-colors ${tab === t ? "bg-black text-white" : "hover:bg-black hover:text-white"}`}>
            {t}
          </button>
        ))}
      </div>

      <main className="px-5 md:px-10 py-10 max-w-5xl">
        {tab === "site" && <SiteEditor />}
        {tab === "tracks" && <TracksEditor />}
        {tab === "shows" && <ShowsEditor />}
        {tab === "gallery" && <GalleryEditor />}
      </main>
    </div>
  );
}

/* ---------- shared inputs ---------- */
function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest font-bold">{label}</span>
      <input {...props} className="mt-1 w-full border-2 border-black px-3 py-2 outline-none focus:bg-black focus:text-white transition-colors" />
    </label>
  );
}
function Area({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest font-bold">{label}</span>
      <textarea {...props} className="mt-1 w-full border-2 border-black px-3 py-2 outline-none focus:bg-black focus:text-white transition-colors min-h-[120px]" />
    </label>
  );
}
const btnPrimary = "inline-flex items-center gap-2 bg-black text-white px-5 py-3 uppercase tracking-widest text-sm border-2 border-black hover:bg-white hover:text-black transition-colors";
const btnGhost = "inline-flex items-center gap-2 border-2 border-black px-4 py-2 uppercase tracking-widest text-xs hover:bg-black hover:text-white transition-colors";

function Heading({ children }) {
  return <h2 className="font-scribble text-4xl md:text-5xl uppercase mb-6">{children}</h2>;
}

/* ---------- SITE ---------- */
function SiteEditor() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/site").then((r) => setData({ socials: [], ...r.data })); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <p className="uppercase tracking-widest">loading...</p>;

  const up = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const upSocial = (i, k, v) => setData((d) => { const s = [...d.socials]; s[i] = { ...s[i], [k]: v }; return { ...d, socials: s }; });
  const addSocial = () => setData((d) => ({ ...d, socials: [...d.socials, { label: "", url: "" }] }));
  const rmSocial = (i) => setData((d) => ({ ...d, socials: d.socials.filter((_, x) => x !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/site", {
        hero_title: data.hero_title, hero_subtitle: data.hero_subtitle, hero_tagline: data.hero_tagline,
        bio: data.bio, whatsapp_number: data.whatsapp_number, socials: data.socials,
      });
      toast.success("Site content saved");
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  return (
    <div>
      <Heading>site content</Heading>
      <div className="space-y-5 border-4 border-black p-6 hard-shadow">
        <Field label="hero title" value={data.hero_title || ""} onChange={(e) => up("hero_title", e.target.value)} data-testid="site-hero-title" />
        <Field label="hero subtitle" value={data.hero_subtitle || ""} onChange={(e) => up("hero_subtitle", e.target.value)} data-testid="site-hero-subtitle" />
        <Field label="hero tagline" value={data.hero_tagline || ""} onChange={(e) => up("hero_tagline", e.target.value)} data-testid="site-hero-tagline" />
        <Area label="bio / gospel" value={data.bio || ""} onChange={(e) => up("bio", e.target.value)} data-testid="site-bio" />
        <Field label="whatsapp number (with country code)" value={data.whatsapp_number || ""} onChange={(e) => up("whatsapp_number", e.target.value)} data-testid="site-whatsapp" />

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-widest font-bold">social links</span>
            <button onClick={addSocial} className={btnGhost} data-testid="add-social-btn"><Plus size={13} strokeWidth={3} /> add</button>
          </div>
          <div className="space-y-3">
            {data.socials.map((s, i) => (
              <div key={i} className="flex gap-2 items-end" data-testid={`social-edit-${i}`}>
                <Field label="label" value={s.label} onChange={(e) => upSocial(i, "label", e.target.value)} />
                <Field label="url" value={s.url} onChange={(e) => upSocial(i, "url", e.target.value)} />
                <button onClick={() => rmSocial(i)} className="border-2 border-black p-2 hover:bg-black hover:text-white transition-colors mb-0.5" data-testid={`remove-social-${i}`}><X size={16} strokeWidth={3} /></button>
              </div>
            ))}
          </div>
        </div>

        <button onClick={save} disabled={saving} className={btnPrimary} data-testid="site-save-btn">
          <Save size={15} strokeWidth={3} /> {saving ? "saving..." : "save changes"}
        </button>
      </div>
    </div>
  );
}

/* ---------- generic list CRUD ---------- */
function useCrud(resource) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get(`/${resource}`).then((r) => setItems(r.data)), [resource]);
  useEffect(() => { load(); }, [load]);
  const create = async (body) => { await api.post(`/${resource}`, body); await load(); };
  const update = async (id, body) => { await api.put(`/${resource}/${id}`, body); await load(); };
  const remove = async (id) => { await api.delete(`/${resource}/${id}`); await load(); };
  return { items, create, update, remove };
}

/* ---------- TRACKS ---------- */
function TracksEditor() {
  const { items, create, update, remove } = useCrud("tracks");
  const empty = { title: "", subtitle: "", link: "", duration: "", order: 0 };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    const body = { ...form, order: Number(form.order) || items.length };
    try {
      if (editing) { await update(editing, body); toast.success("Track updated"); }
      else { await create(body); toast.success("Track added"); }
      setForm(empty); setEditing(null);
    } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <Heading>tracks</Heading>
      <div className="border-4 border-black p-6 hard-shadow space-y-4 mb-8">
        <p className="text-xs uppercase tracking-widest font-bold">{editing ? "edit track" : "new track"}</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="track-title-input" />
          <Field label="subtitle / producer" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} data-testid="track-subtitle-input" />
          <Field label="link (spotify/youtube)" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} data-testid="track-link-input" />
          <Field label="duration (e.g. 2:54)" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} data-testid="track-duration-input" />
        </div>
        <div className="flex gap-3">
          <button onClick={save} className={btnPrimary} data-testid="track-save-btn"><Save size={15} strokeWidth={3} /> {editing ? "update" : "add track"}</button>
          {editing && <button onClick={() => { setForm(empty); setEditing(null); }} className={btnGhost}>cancel</button>}
        </div>
      </div>

      <div className="border-4 border-black" data-testid="admin-tracks-list">
        {items.map((t, i) => (
          <div key={t.id} className={`flex items-center gap-4 px-5 py-4 ${i !== items.length - 1 ? "border-b-4 border-black" : ""}`} data-testid={`admin-track-${i}`}>
            <span className="font-scribble text-2xl w-8">{String(i + 1).padStart(2, "0")}</span>
            <div className="flex-1 min-w-0">
              <p className="font-bold uppercase truncate">{t.title}</p>
              <p className="text-xs uppercase opacity-70 truncate">{t.subtitle} {t.duration && `· ${t.duration}`}</p>
            </div>
            <button onClick={() => { setForm({ ...t }); setEditing(t.id); }} className={btnGhost} data-testid={`edit-track-${i}`}>edit</button>
            <button onClick={() => remove(t.id)} className="border-2 border-black p-2 hover:bg-black hover:text-white transition-colors" data-testid={`delete-track-${i}`}><Trash2 size={15} strokeWidth={2.5} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="p-6 uppercase tracking-widest text-sm">no tracks yet.</p>}
      </div>
    </div>
  );
}

/* ---------- SHOWS ---------- */
function ShowsEditor() {
  const { items, create, update, remove } = useCrud("shows");
  const empty = { date: "", city: "", venue: "", ticket_link: "", sold_out: false, order: 0 };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const save = async () => {
    if (!form.date.trim() || !form.city.trim()) return toast.error("Date and city required");
    const body = { ...form, order: Number(form.order) || items.length };
    try {
      if (editing) { await update(editing, body); toast.success("Show updated"); }
      else { await create(body); toast.success("Show added"); }
      setForm(empty); setEditing(null);
    } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <Heading>shows</Heading>
      <div className="border-4 border-black p-6 hard-shadow space-y-4 mb-8">
        <p className="text-xs uppercase tracking-widest font-bold">{editing ? "edit show" : "new show"}</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="show-date-input" />
          <Field label="city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="show-city-input" />
          <Field label="venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} data-testid="show-venue-input" />
          <Field label="ticket link" value={form.ticket_link} onChange={(e) => setForm({ ...form, ticket_link: e.target.value })} data-testid="show-ticket-input" />
        </div>
        <label className="flex items-center gap-3 text-xs uppercase tracking-widest font-bold cursor-pointer">
          <input type="checkbox" checked={form.sold_out} onChange={(e) => setForm({ ...form, sold_out: e.target.checked })} className="w-5 h-5 accent-black" data-testid="show-soldout-input" />
          sold out
        </label>
        <div className="flex gap-3">
          <button onClick={save} className={btnPrimary} data-testid="show-save-btn"><Save size={15} strokeWidth={3} /> {editing ? "update" : "add show"}</button>
          {editing && <button onClick={() => { setForm(empty); setEditing(null); }} className={btnGhost}>cancel</button>}
        </div>
      </div>

      <div className="border-4 border-black" data-testid="admin-shows-list">
        {items.map((s, i) => (
          <div key={s.id} className={`flex items-center gap-4 px-5 py-4 ${i !== items.length - 1 ? "border-b-4 border-black" : ""}`} data-testid={`admin-show-${i}`}>
            <span className="font-scribble text-2xl w-24 shrink-0">{s.date}</span>
            <div className="flex-1 min-w-0">
              <p className="font-bold uppercase truncate">{s.city} {s.sold_out && <span className="text-xs line-through">(sold out)</span>}</p>
              <p className="text-xs uppercase opacity-70 truncate">{s.venue}</p>
            </div>
            <button onClick={() => { setForm({ ...s }); setEditing(s.id); }} className={btnGhost} data-testid={`edit-show-${i}`}>edit</button>
            <button onClick={() => remove(s.id)} className="border-2 border-black p-2 hover:bg-black hover:text-white transition-colors" data-testid={`delete-show-${i}`}><Trash2 size={15} strokeWidth={2.5} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="p-6 uppercase tracking-widest text-sm">no shows yet.</p>}
      </div>
    </div>
  );
}

/* ---------- GALLERY ---------- */
function GalleryEditor() {
  const { items, create, update, remove } = useCrud("gallery");
  const empty = { image_url: "", caption: "", order: 0 };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 10 * 1024 * 1024) return toast.error("Image must be under 10MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  const save = async () => {
    if (!form.image_url.trim()) return toast.error("Upload an image or paste a URL first");
    const body = { ...form, order: Number(form.order) || items.length };
    try {
      if (editing) { await update(editing, body); toast.success("Item updated"); }
      else { await create(body); toast.success("Item added"); }
      setForm(empty); setEditing(null);
    } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <Heading>gallery</Heading>
      <div className="border-4 border-black p-6 hard-shadow space-y-4 mb-8">
        <p className="text-xs uppercase tracking-widest font-bold">{editing ? "edit item" : "new item"}</p>

        <div>
          <span className="text-xs uppercase tracking-widest font-bold">upload image</span>
          <label className={`mt-1 flex items-center justify-center gap-3 border-2 border-dashed border-black px-4 py-6 uppercase tracking-widest text-sm cursor-pointer hover:bg-black hover:text-white transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="gallery-upload-label">
            <Upload size={16} strokeWidth={2.5} /> {uploading ? "uploading..." : "choose an image (jpg / png / webp)"}
            <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} data-testid="gallery-file-input" />
          </label>
        </div>

        <Field label="or paste an image url" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="gallery-url-input" />
        <Field label="caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} data-testid="gallery-caption-input" />
        {form.image_url && <img src={resolveImg(form.image_url)} alt="preview" className="bw-img w-32 h-32 object-cover border-2 border-black" />}
        <div className="flex gap-3">
          <button onClick={save} className={btnPrimary} data-testid="gallery-save-btn"><Save size={15} strokeWidth={3} /> {editing ? "update" : "add item"}</button>
          {editing && <button onClick={() => { setForm(empty); setEditing(null); }} className={btnGhost}>cancel</button>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-gallery-list">
        {items.map((g, i) => (
          <div key={g.id} className="border-4 border-black" data-testid={`admin-gallery-${i}`}>
            <img src={resolveImg(g.image_url)} alt={g.caption} className="bw-img w-full aspect-square object-cover border-b-4 border-black" />
            <p className="px-2 py-1 text-xs uppercase truncate font-bold">{g.caption || "untitled"}</p>
            <div className="flex border-t-4 border-black">
              <button onClick={() => { setForm({ ...g }); setEditing(g.id); }} className="flex-1 py-2 text-xs uppercase tracking-widest border-r-4 border-black hover:bg-black hover:text-white transition-colors" data-testid={`edit-gallery-${i}`}>edit</button>
              <button onClick={() => remove(g.id)} className="flex-1 py-2 flex items-center justify-center hover:bg-black hover:text-white transition-colors" data-testid={`delete-gallery-${i}`}><Trash2 size={14} strokeWidth={2.5} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="p-6 uppercase tracking-widest text-sm">gallery empty.</p>}
      </div>
    </div>
  );
}
